// NORMALTON SQUARE — the one map. Pure data + the geometry queries the sim needs (static collision, line of sight,
// a 0.5 m nav grid with A* and path smoothing). No rendering here: the renderer places meshes from LAYOUT
// (id + kind + transform) and reads the semantic tables (STALLS, BENCHES, DRAINS, ...) for anything it animates.
//
// Conventions (shared with RIG.md): metres, Y up, ground at y = 0. x runs east, z runs south (north is -z).
// A yaw `rot` turns local +Z (an object's FRONT, a character's facing) to the world direction (sin rot, cos rot).
//
//            north: facades
//     ┌───────────── bandstand ─────────────┐
//     │ stall                         stall │
//     │ stall    benches ◯ fountain   stall │      the bus pulls up on the road below the south edge;
//     │ stall                         stall │      its door opens onto the square at BUS.door
//     └──────────── bus stop ── road ───────┘
import { SQUARE_HALF, NAV_CELL, NAV_CLEAR, BODY_R, FLOAT_LOOP_R, FLOAT_R } from "./tuning";

export type V2 = { x: number; z: number };
export type Placement = { id: string; kind: PropKind; x: number; z: number; rot: number; w?: number; d?: number; r?: number; h?: number; variant?: number };
export type PropKind = "stall" | "fountain" | "bench" | "tree" | "lamp" | "flowerbed" | "bandstand" | "bus_stop" | "drain" | "facade" | "road" | "ground";
/** a static collider. `tall` ones block line of sight (the Inspector's eyes, the bots' perception). */
export type Collider =
  | { k: "circle"; x: number; z: number; r: number; tall: boolean }
  | { k: "box"; x: number; z: number; hw: number; hd: number; rot: number; tall: boolean };
export type PoiKind = "browse" | "seat" | "plaza" | "rim" | "drain" | "bus";
/** a Point of Interest: where an NPC routine goes. `at` is where the body stands (for a seat: the bench itself),
 *  `face` the heading it takes there, `ref` the stall / bench / drain index it belongs to. */
export type Poi = { id: number; kind: PoiKind; x: number; z: number; face: number; ref: number; approach?: V2 };

/** local (lx, lz) of an object at (x, z) with yaw rot → world */
export function toWorld(x: number, z: number, rot: number, lx: number, lz: number): V2 {
  const c = Math.cos(rot), s = Math.sin(rot);
  return { x: x + lx * c + lz * s, z: z - lx * s + lz * c };
}
/** heading (yaw) that faces from a toward b */
export const headingTo = (ax: number, az: number, bx: number, bz: number) => Math.atan2(bx - ax, bz - az);
const r2 = (n: number) => Math.round(n * 100) / 100;

// ---------------------------------------------------------------------------------------------------------------------
// The layout tables

/** six market stalls: two rows of three, fronts facing the plaza. Each sells its own thing (variant = stall type). */
export const STALLS = [
  { x: -14, z: -7, rot: Math.PI / 2 }, { x: -14, z: 0, rot: Math.PI / 2 }, { x: -14, z: 7, rot: Math.PI / 2 },
  { x: 14, z: -7, rot: -Math.PI / 2 }, { x: 14, z: 0, rot: -Math.PI / 2 }, { x: 14, z: 7, rot: -Math.PI / 2 },
].map((s, i) => {
  const W = 2.6, D = 1.6;
  const counterA = toWorld(s.x, s.z, s.rot, -W / 2 + 0.2, D / 2), counterB = toWorld(s.x, s.z, s.rot, W / 2 - 0.2, D / 2);
  return {
    i, ...s, w: W, d: D,
    /** the capsule sits on the counter here (y = 1.0) */
    capsule: toWorld(s.x, s.z, s.rot, 0, D / 2 - 0.25),
    /** the counter's front edge: grab range is measured to this segment */
    counterA, counterB,
    /** where a shopper stands (3 spots), facing the counter */
    spots: [-0.8, 0, 0.8].map((o) => toWorld(s.x, s.z, s.rot, o, D / 2 + 0.55)),
  };
});

export const FOUNTAIN = { x: 0, z: 0, r: 2.4, columnR: 0.6 };
export const BANDSTAND = { x: 0, z: -13.5, r: 2.8, floorY: 0.9 };
/** the Town Crier stands here (on the bandstand floor) facing the square; he rings the bell on every real call */
export const CRIER = { x: 0, z: -12.6, y: BANDSTAND.floorY, rot: 0 };

/** benches: four round the fountain facing in, two audience benches facing the bandstand, two corner benches */
export const BENCHES = (() => {
  const out: { x: number; z: number; rot: number }[] = [];
  for (const deg of [45, 135, 225, 315]) {
    const t = (deg * Math.PI) / 180, x = 5.6 * Math.cos(t), z = 5.6 * Math.sin(t);
    out.push({ x: r2(x), z: r2(z), rot: Math.atan2(-x, -z) });
  }
  out.push({ x: -4.6, z: -9.9, rot: Math.PI }, { x: 4.6, z: -9.9, rot: Math.PI });
  out.push({ x: -12.4, z: 14.8, rot: Math.PI }, { x: 12.4, z: -14.6, rot: 0 });
  return out.map((b, i) => ({
    i, ...b, w: 1.8, d: 0.55,
    /** where a sitter's root sits (on the bench, hips at 0.38 per RIG) and the spot in front they walk to first */
    seats: [-0.45, 0.45].map((o) => ({ at: toWorld(b.x, b.z, b.rot, o, 0), approach: toWorld(b.x, b.z, b.rot, o, 0.85) })),
  }));
})();

export const DRAINS = [{ x: -3.0, z: -10.6 }, { x: 3.2, z: -10.8 }, { x: -3.0, z: 10.8 }, { x: 4.4, z: 10.0 }].map((d, i) => ({ i, ...d }));
export const TREES = [{ x: -15, z: -15 }, { x: 15, z: -15.6 }, { x: -15.2, z: 15.4 }, { x: 15.4, z: 13.8 }, { x: -4.6, z: 12.4 }, { x: 3.6, z: 13.2 }, { x: -9.6, z: -14.6 }, { x: 8.4, z: -15.8 }];
export const LAMPS = [{ x: -9.2, z: -4 }, { x: 9.2, z: -4 }, { x: -9.2, z: 4 }, { x: 9.2, z: 4 }, { x: -4.2, z: -16.4 }, { x: 4.2, z: -16.4 }, { x: -11.5, z: 16.6 }, { x: 13.2, z: 16.4 }];
export const FLOWERBEDS = [{ x: 0, z: 11.6, w: 3.2, d: 1.0 }, { x: -16.4, z: 11, w: 1.0, d: 3.0 }, { x: 16.4, z: -11, w: 1.0, d: 3.0 }, { x: -6.4, z: 16.2, w: 3.0, d: 1.0 }];

/** the bus: it pulls along the road south of the square and stops with its door at `door`.
 *  `from` / `to` are where it enters and leaves view; the renderer drives along x at z = road. */
export const BUS = { road: 20.0, stopX: 9.5, from: -32, to: 32, door: { x: 10.6, z: 17.3 }, shelter: { x: 5.6, z: 17.2, rot: Math.PI, w: 2.6, d: 0.8 } };

// ---------------------------------------------------------------------------------------------------------------------
// Colliders (built from the tables above)

export const COLLIDERS: Collider[] = [
  ...STALLS.map((s): Collider => ({ k: "box", x: s.x, z: s.z, hw: s.w / 2, hd: s.d / 2, rot: s.rot, tall: true })),
  { k: "circle", x: FOUNTAIN.x, z: FOUNTAIN.z, r: FOUNTAIN.r, tall: false },
  { k: "circle", x: FOUNTAIN.x, z: FOUNTAIN.z, r: FOUNTAIN.columnR, tall: true },
  { k: "circle", x: BANDSTAND.x, z: BANDSTAND.z, r: BANDSTAND.r, tall: false },
  ...BENCHES.map((b): Collider => ({ k: "box", x: b.x, z: b.z, hw: b.w / 2, hd: b.d / 2, rot: b.rot, tall: false })),
  ...TREES.map((t): Collider => ({ k: "circle", x: t.x, z: t.z, r: 0.45, tall: true })),
  ...LAMPS.map((l): Collider => ({ k: "circle", x: l.x, z: l.z, r: 0.15, tall: false })),
  ...FLOWERBEDS.map((f): Collider => ({ k: "box", x: f.x, z: f.z, hw: f.w / 2, hd: f.d / 2, rot: 0, tall: false })),
  { k: "box", x: BUS.shelter.x, z: BUS.shelter.z, hw: BUS.shelter.w / 2, hd: BUS.shelter.d / 2, rot: BUS.shelter.rot, tall: true },
];

// ---------------------------------------------------------------------------------------------------------------------
// Points of Interest

export const POIS: Poi[] = [];
{
  const add = (kind: PoiKind, x: number, z: number, face: number, ref = -1, approach?: V2) => POIS.push({ id: POIS.length, kind, x: r2(x), z: r2(z), face, ref, approach });
  for (const s of STALLS) for (const p of s.spots) add("browse", p.x, p.z, s.rot + Math.PI, s.i);
  for (const b of BENCHES) for (const s of b.seats) add("seat", s.at.x, s.at.z, b.rot, b.i, s.approach);
  for (let k = 0; k < 8; k++) { const t = (k / 8) * Math.PI * 2 + Math.PI / 8, R = FOUNTAIN.r + 0.55; add("rim", R * Math.cos(t), R * Math.sin(t), Math.atan2(-Math.cos(t), -Math.sin(t))); }
  for (const d of DRAINS) { const away = Math.atan2(-d.x, -d.z); add("drain", d.x + 0.7 * Math.sin(away), d.z + 0.7 * Math.cos(away), away + Math.PI, d.i); }
  // open plaza spots: chats, idling, looking around (hand-placed in the lanes; the self-check proves each is walkable)
  const PLAZA: [number, number][] = [[-6, -5], [6, -5], [-6, 5.5], [6, 5.5], [0, -7.5], [-3, 8.5], [3.5, 8.6], [-10.5, -1], [10.5, 1], [-10.6, 7.5],
    [10.6, -7.2], [-5.5, -12.5], [5.6, -12.2], [-11, 12.5], [11.5, 12.6], [0, 15.5], [-11.8, -11.4], [12, -10.8], [-2.5, -16.6], [7.5, 14.2]];
  for (const [x, z] of PLAZA) add("plaza", x, z, Math.atan2(-x, -z));
  for (const [x, z] of [[8.2, 16.2], [9.4, 16.4], [11.8, 16.2]] as [number, number][]) add("bus", x, z, 0);
}
export const poisOf = (k: PoiKind) => POIS.filter((p) => p.kind === k);

// ---------------------------------------------------------------------------------------------------------------------
// The renderer's placement list: one row per mesh to place

export const LAYOUT: Placement[] = (() => {
  const L: Placement[] = [];
  L.push({ id: "ground", kind: "ground", x: 0, z: 0, rot: 0, w: SQUARE_HALF * 2, d: SQUARE_HALF * 2 });
  L.push({ id: "road", kind: "road", x: 0, z: BUS.road, rot: 0, w: 64, d: 4 });
  STALLS.forEach((s) => L.push({ id: `stall_${s.i}`, kind: "stall", x: s.x, z: s.z, rot: s.rot, w: s.w, d: s.d, variant: s.i }));
  L.push({ id: "fountain", kind: "fountain", x: FOUNTAIN.x, z: FOUNTAIN.z, rot: 0, r: FOUNTAIN.r });
  L.push({ id: "bandstand", kind: "bandstand", x: BANDSTAND.x, z: BANDSTAND.z, rot: 0, r: BANDSTAND.r, h: BANDSTAND.floorY });
  BENCHES.forEach((b) => L.push({ id: `bench_${b.i}`, kind: "bench", x: b.x, z: b.z, rot: b.rot, w: b.w, d: b.d }));
  DRAINS.forEach((d) => L.push({ id: `drain_${d.i}`, kind: "drain", x: d.x, z: d.z, rot: 0 }));
  TREES.forEach((t, i) => L.push({ id: `tree_${i}`, kind: "tree", x: t.x, z: t.z, rot: (i * 1.7) % (Math.PI * 2), variant: i % 3 }));
  LAMPS.forEach((l, i) => L.push({ id: `lamp_${i}`, kind: "lamp", x: l.x, z: l.z, rot: 0 }));
  FLOWERBEDS.forEach((f, i) => L.push({ id: `flowerbed_${i}`, kind: "flowerbed", x: f.x, z: f.z, rot: 0, w: f.w, d: f.d, variant: i % 2 }));
  L.push({ id: "bus_stop", kind: "bus_stop", x: BUS.shelter.x, z: BUS.shelter.z, rot: BUS.shelter.rot, w: BUS.shelter.w, d: BUS.shelter.d });
  // facades: a ring of 6 m shopfronts facing in (north, west, east), and a row across the road to the south
  const H = SQUARE_HALF, depth = 3;
  for (let k = 0; k < 6; k++) {
    const a = -H + 3 + k * 6;
    L.push({ id: `facade_n${k}`, kind: "facade", x: a, z: -H - depth / 2, rot: 0, w: 6, d: depth, variant: k % 4 });
    L.push({ id: `facade_w${k}`, kind: "facade", x: -H - depth / 2, z: a, rot: Math.PI / 2, w: 6, d: depth, variant: (k + 1) % 4 });
    L.push({ id: `facade_e${k}`, kind: "facade", x: H + depth / 2, z: a, rot: -Math.PI / 2, w: 6, d: depth, variant: (k + 2) % 4 });
    L.push({ id: `facade_s${k}`, kind: "facade", x: a, z: BUS.road + 2 + depth / 2, rot: Math.PI, w: 6, d: depth, variant: (k + 3) % 4 });
  }
  return L;
})();

// ---------------------------------------------------------------------------------------------------------------------
// Static collision

/** push a body of radius R out of every static collider and back inside the square. Returns the corrected position. */
export function collideStatic(x: number, z: number, R: number): V2 {
  for (let pass = 0; pass < 2; pass++) {
    for (const c of bucketAt(x, z)) {
      if (c.k === "circle") {
        const dx = x - c.x, dz = z - c.z, d = Math.hypot(dx, dz), min = c.r + R;
        if (d < min) { if (d < 1e-6) { x = c.x + min; } else { x = c.x + (dx / d) * min; z = c.z + (dz / d) * min; } }
      } else {
        const co = Math.cos(c.rot), si = Math.sin(c.rot), dx = x - c.x, dz = z - c.z;
        const lx = dx * co - dz * si, lz = dx * si + dz * co;   // world → local
        const cx = Math.max(-c.hw, Math.min(c.hw, lx)), cz = Math.max(-c.hd, Math.min(c.hd, lz));
        let ox = lx - cx, oz = lz - cz; const d = Math.hypot(ox, oz);
        if (d >= R) continue;
        let nlx: number, nlz: number;
        if (d > 1e-6) { nlx = cx + (ox / d) * R; nlz = cz + (oz / d) * R; }
        else {   // centre inside the box: leave by the nearest face
          const px = c.hw - Math.abs(lx), pz = c.hd - Math.abs(lz);
          if (px < pz) { nlx = Math.sign(lx || 1) * (c.hw + R); nlz = lz; } else { nlx = lx; nlz = Math.sign(lz || 1) * (c.hd + R); }
        }
        x = c.x + nlx * co + nlz * si; z = c.z - nlx * si + nlz * co;   // local → world
      }
    }
  }
  const lim = SQUARE_HALF - R;
  return { x: Math.max(-lim, Math.min(lim, x)), z: Math.max(-lim, Math.min(lim, z)) };
}

/** distance from p to the nearest point of a collider's surface (negative inside) */
function sdf(c: Collider, x: number, z: number): number {
  if (c.k === "circle") return Math.hypot(x - c.x, z - c.z) - c.r;
  const co = Math.cos(c.rot), si = Math.sin(c.rot), dx = x - c.x, dz = z - c.z;
  const lx = Math.abs(dx * co - dz * si) - c.hw, lz = Math.abs(dx * si + dz * co) - c.hd;
  return Math.hypot(Math.max(lx, 0), Math.max(lz, 0)) + Math.min(Math.max(lx, lz), 0);
}

// colliders bucketed on a coarse 3 m grid so a body only tests its neighbours
const BK = 3, BN = Math.ceil((SQUARE_HALF * 2) / BK) + 2;
const buckets: Collider[][] = Array.from({ length: BN * BN }, () => []);
{
  const pad = 1.0;
  for (const c of COLLIDERS) {
    const ext = c.k === "circle" ? c.r : Math.hypot(c.hw, c.hd);
    for (let bx = bIdx(c.x - ext - pad); bx <= bIdx(c.x + ext + pad); bx++)
      for (let bz = bIdx(c.z - ext - pad); bz <= bIdx(c.z + ext + pad); bz++) buckets[bz * BN + bx].push(c);
  }
}
function bIdx(v: number) { return Math.max(0, Math.min(BN - 1, Math.floor((v + SQUARE_HALF) / BK) + 1)); }
function bucketAt(x: number, z: number) { return buckets[bIdx(z) * BN + bIdx(x)]; }

// ---------------------------------------------------------------------------------------------------------------------
// Nav grid (0.5 m cells): a cell is blocked when a body standing at its centre would overlap a collider

export const NAV_N = Math.round((SQUARE_HALF * 2) / NAV_CELL);   // 72
export const navBlocked = new Uint8Array(NAV_N * NAV_N);
/** tall colliders rasterised for line of sight (no inflation) */
const losBlocked = new Uint8Array(NAV_N * NAV_N);
export const cellX = (i: number) => -SQUARE_HALF + NAV_CELL * (i + 0.5);
export const cellOf = (v: number) => Math.max(0, Math.min(NAV_N - 1, Math.floor((v + SQUARE_HALF) / NAV_CELL)));
{
  const inflate = BODY_R + NAV_CLEAR, lim = SQUARE_HALF - BODY_R;
  for (let j = 0; j < NAV_N; j++) for (let i = 0; i < NAV_N; i++) {
    const x = cellX(i), z = cellX(j);
    let b = Math.abs(x) > lim || Math.abs(z) > lim ? 1 : 0, t = 0;
    for (const c of COLLIDERS) { const d = sdf(c, x, z); if (d < inflate) b = 1; if (c.tall && d < 0) t = 1; }
    navBlocked[j * NAV_N + i] = b; losBlocked[j * NAV_N + i] = t;
  }
}
export const blockedAt = (x: number, z: number) => navBlocked[cellOf(z) * NAV_N + cellOf(x)] === 1;

/** is the straight line a→b clear of tall colliders? (the Inspector can see / pop along it) */
export function losClear(ax: number, az: number, bx: number, bz: number): boolean {
  const d = Math.hypot(bx - ax, bz - az), n = Math.ceil(d / 0.2);
  for (let k = 1; k < n; k++) { const t = k / n; if (losBlocked[cellOf(az + (bz - az) * t) * NAV_N + cellOf(ax + (bx - ax) * t)]) return false; }
  return true;
}
/** can a body walk straight from a to b? (sampled on the inflated nav grid) */
export function walkClear(ax: number, az: number, bx: number, bz: number): boolean {
  const d = Math.hypot(bx - ax, bz - az), n = Math.max(1, Math.ceil(d / 0.2));
  for (let k = 0; k <= n; k++) { const t = k / n; if (blockedAt(ax + (bx - ax) * t, az + (bz - az) * t)) return false; }
  return true;
}

/** the nearest walkable cell centre to a point (ring search) */
export function nearestFree(x: number, z: number): V2 {
  const ci = cellOf(x), cj = cellOf(z);
  if (!navBlocked[cj * NAV_N + ci]) return { x, z };
  for (let r = 1; r < NAV_N; r++) {
    let best = -1, bd = Infinity;
    for (let j = cj - r; j <= cj + r; j++) for (let i = ci - r; i <= ci + r; i++) {
      if (i < 0 || j < 0 || i >= NAV_N || j >= NAV_N || (Math.abs(i - ci) !== r && Math.abs(j - cj) !== r)) continue;
      if (navBlocked[j * NAV_N + i]) continue;
      const d = (cellX(i) - x) ** 2 + (cellX(j) - z) ** 2; if (d < bd) { bd = d; best = j * NAV_N + i; }
    }
    if (best >= 0) return { x: cellX(best % NAV_N), z: cellX(Math.floor(best / NAV_N)) };
  }
  return { x: 0, z: 8 };
}

// A* scratch (one search at a time; the sim is single-threaded)
const G = new Float32Array(NAV_N * NAV_N), PARENT = new Int32Array(NAV_N * NAV_N), STAMP = new Uint32Array(NAV_N * NAV_N), CLOSED = new Uint32Array(NAV_N * NAV_N);
let stamp = 0;
const heap: number[] = [], heapF: number[] = [];
function hpush(n: number, f: number) { heap.push(n); heapF.push(f); let i = heap.length - 1; while (i > 0) { const p = (i - 1) >> 1; if (heapF[p] <= heapF[i]) break; [heap[p], heap[i]] = [heap[i], heap[p]]; [heapF[p], heapF[i]] = [heapF[i], heapF[p]]; i = p; } }
function hpop(): number {
  const top = heap[0], ln = heap.pop()!, lf = heapF.pop()!;
  if (heap.length) { heap[0] = ln; heapF[0] = lf; let i = 0; for (;;) { const l = i * 2 + 1, r = l + 1; let m = i; if (l < heap.length && heapF[l] < heapF[m]) m = l; if (r < heap.length && heapF[r] < heapF[m]) m = r; if (m === i) break; [heap[m], heap[i]] = [heap[i], heap[m]]; [heapF[m], heapF[i]] = [heapF[i], heapF[m]]; i = m; } }
  return top;
}
const DIRS = [[1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1], [1, 1, Math.SQRT2], [1, -1, Math.SQRT2], [-1, 1, Math.SQRT2], [-1, -1, Math.SQRT2]];

/** A* over the nav grid (8-connected, no corner cutting), then string-pulled into a few straight legs.
 *  Returns waypoints from (just after) the start to the goal; [] when start and goal are the same cell;
 *  null when the goal is unreachable. Start / goal inside an obstacle are first moved to the nearest free cell. */
export function findPath(sx: number, sz: number, gx: number, gz: number): V2[] | null {
  const s = nearestFree(sx, sz), g = nearestFree(gx, gz);
  const si = cellOf(s.x), sj = cellOf(s.z), gi = cellOf(g.x), gj = cellOf(g.z);
  const start = sj * NAV_N + si, goal = gj * NAV_N + gi;
  const goalPt = blockedAt(gx, gz) ? g : { x: gx, z: gz };
  if (start === goal) return [goalPt];
  if (walkClear(sx, sz, goalPt.x, goalPt.z)) return [goalPt];
  stamp++; heap.length = 0; heapF.length = 0;
  const h = (i: number, j: number) => { const dx = Math.abs(i - gi), dz = Math.abs(j - gj); return Math.max(dx, dz) + (Math.SQRT2 - 1) * Math.min(dx, dz); };
  G[start] = 0; STAMP[start] = stamp; PARENT[start] = -1; hpush(start, h(si, sj));
  let found = false;
  while (heap.length) {
    const n = hpop(); if (CLOSED[n] === stamp) continue; CLOSED[n] = stamp;
    if (n === goal) { found = true; break; }
    const ni = n % NAV_N, nj = (n - ni) / NAV_N;
    for (const [dx, dz, cost] of DIRS) {
      const i = ni + dx, j = nj + dz;
      if (i < 0 || j < 0 || i >= NAV_N || j >= NAV_N) continue;
      const m = j * NAV_N + i; if (navBlocked[m] || CLOSED[m] === stamp) continue;
      if (dx && dz && (navBlocked[nj * NAV_N + i] || navBlocked[j * NAV_N + ni])) continue;   // no corner cutting
      const gn = G[n] + cost;
      if (STAMP[m] !== stamp || gn < G[m]) { STAMP[m] = stamp; G[m] = gn; PARENT[m] = n; hpush(m, gn + h(i, j)); }
    }
  }
  if (!found) return null;
  const cells: V2[] = [];
  for (let n = goal; n !== -1; n = PARENT[n]) cells.push({ x: cellX(n % NAV_N), z: cellX(Math.floor(n / NAV_N)) });
  cells.reverse(); cells[cells.length - 1] = goalPt;
  return smooth(sx, sz, cells);
}

/** string pulling: from where we stand, jump to the farthest waypoint we can walk to in a straight line */
function smooth(sx: number, sz: number, pts: V2[]): V2[] {
  const out: V2[] = []; let cx = sx, cz = sz, i = 0;
  while (i < pts.length) {
    let far = i;
    for (let k = pts.length - 1; k > i; k--) if (walkClear(cx, cz, pts[k].x, pts[k].z)) { far = k; break; }
    out.push(pts[far]); cx = pts[far].x; cz = pts[far].z; i = far + 1;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------------
// Geometry helpers the sim uses for verbs

/** distance from p to the segment a-b */
export function segDist(px: number, pz: number, a: V2, b: V2): number {
  const abx = b.x - a.x, abz = b.z - a.z, t = Math.max(0, Math.min(1, ((px - a.x) * abx + (pz - a.z) * abz) / (abx * abx + abz * abz || 1)));
  return Math.hypot(px - (a.x + abx * t), pz - (a.z + abz * t));
}
/** the stall whose counter is within `range` of p, or -1 */
export function stallNear(x: number, z: number, range: number): number {
  let best = -1, bd = range;
  for (const s of STALLS) { const d = segDist(x, z, s.counterA, s.counterB); if (d <= bd) { bd = d; best = s.i; } }
  return best;
}
/** the drain within `range` of p, or -1 */
export function drainNear(x: number, z: number, range: number): number {
  let best = -1, bd = range;
  for (const d of DRAINS) { const dd = Math.hypot(x - d.x, z - d.z); if (dd <= bd) { bd = dd; best = d.i; } }
  return best;
}

// ---------------------------------------------------------------------------------------------------------------------
// The parade float's loop: a circle round the fountain, run clockwise seen from above. `s` is arc length (m).

export const FLOAT_LOOP_LEN = 2 * Math.PI * FLOAT_LOOP_R;
export function floatAt(s: number): { x: number; z: number; rot: number } {
  const a = ((s % FLOAT_LOOP_LEN) + FLOAT_LOOP_LEN) % FLOAT_LOOP_LEN / FLOAT_LOOP_R;
  const x = FLOAT_LOOP_R * Math.cos(a), z = FLOAT_LOOP_R * Math.sin(a);
  return { x, z, rot: Math.atan2(-Math.sin(a), Math.cos(a)) };   // tangent of increasing a
}
/** the loop must clear every static collider by the float's radius; the gates call this (map self-check) */
export function floatLoopClear(): { ok: boolean; worst: number } {
  let worst = Infinity;
  for (let s = 0; s < FLOAT_LOOP_LEN; s += 0.25) { const p = floatAt(s); for (const c of COLLIDERS) worst = Math.min(worst, sdf(c, p.x, p.z) - FLOAT_R); }
  return { ok: worst > 0, worst };
}
/** every POI must stand on walkable ground (seats excepted: they ARE the bench) and every walkable POI must reach the bus door */
export function mapSelfCheck(): string[] {
  const bad: string[] = [];
  for (const p of POIS) {
    const at = p.approach || p;
    if (blockedAt(at.x, at.z)) bad.push(`poi ${p.id} (${p.kind}) at ${at.x},${at.z} is inside an obstacle`);
    else if (!findPath(BUS.door.x, BUS.door.z, at.x, at.z)) bad.push(`poi ${p.id} (${p.kind}) unreachable from the bus`);
  }
  if (blockedAt(BUS.door.x, BUS.door.z)) bad.push("bus door blocked");
  if (blockedAt(CRIER.x, CRIER.z + BANDSTAND.r + 0.6)) bad.push("bandstand front blocked");
  const f = floatLoopClear(); if (!f.ok) bad.push(`float loop clips a collider (worst clearance ${f.worst.toFixed(2)} m)`);
  return bad;
}
